import React, { useMemo, useState } from "react";
import { Check } from "lucide-react";
import { logger } from "@/utils/logger";
import { Toaster } from "@/components/ui/toaster";
import { useStripePricesQuery } from "@/api/vercel";
import {
  PRODUCT_LEXI_SOCIAL,
  PRODUCT_MELBOURNE_CUP_PROMO,
  PRODUCT_WEEKPASS,
  PRODUCT_LEXI_PREMIUM,
  SHOW_VIP_PROMOS,
} from "@/utils/vars";
import AuthModal from "@/components/auth-modal";
import { raceDayTipsTime } from "@/utils/time-format";

// Sydney time with its zone, like /lexi-pro: "11:00 am AEDT" in daylight saving, "10:00 am AEST" in winter
const RACE_DAY_TIME = raceDayTipsTime();

const parseMarketingFeatures = (product) => {
  const marketingFeatures = product?.marketingFeatures || {};
  return marketingFeatures;
};

// The tiers are every active Stripe product with a price. While VIP promos are off, a VIP,
// Premium or Platinum product never shows here, even if someone makes one active in Stripe.
// ("Lexi Pro" is PRODUCT_LEXI_PREMIUM and is not affected.)
const VIP_TIER_NAME = /\b(vip|premium|platinum)\b/i;

/*
 * Plan copy (owner, 2 Nov 2026: pricing looks like version A of the home page
 * makeover, three plain cards with Lexi Pro highlighted). Names and prices
 * still come live from Stripe; what each plan includes is written here because
 * the Stripe marketing features are out of date ("twice a day", "Morning
 * tips", "Analysis"). Any other product falls back to its Stripe text.
 */
export const PLAN_COPY = {
  social: {
    blurb: "The Race Day Top Tip and tip 2 for every race.",
    features: [
      `Race Day Top Tip and tip 2, by ${RACE_DAY_TIME}`,
      "Every Australian thoroughbred race",
      "Public race results",
      "No credit card needed",
    ],
    button: "Get tips now!",
    gtm: "pricing_social_button-1",
  },
  weekPass: {
    blurb: "All of Lexi Pro for 7 days. No subscription.",
    features: [
      "All 12 tips per race",
      "Early Mail, Race Day tips and Late Mail",
      "Lexi Chat on every race",
    ],
    button: "Get the 7-Day Pass",
    gtm: "pricing_weekpass_button-1",
  },
  pro: {
    blurb: "Every tip set for every race, plus Lexi Chat.",
    features: [
      "4 Early Mail tips per race, up to 3 days out",
      `4 Race Day tips per race, by ${RACE_DAY_TIME}`,
      "4 Late Mail tips per race, minutes before the jump",
      "Lexi Chat on every race",
      "Movement arrows and each race's tip history",
    ],
    gtm: "pricing_pro_button-1",
  },
};

const formatAmount = (amount) =>
  Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`;

/**
 * PricingSection: the live Stripe plans as three plain cards, Lexi Pro
 * highlighted with the "Best value" badge (owner: keep).
 */
function PricingSection() {
  // Fetch Stripe prices
  const { data: stripePricesData, isLoading, error } = useStripePricesQuery();

  // Auth modal state
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [selectedPlanForModal, setSelectedPlanForModal] = useState("pro");

  // Transform Stripe data into pricing tiers
  const pricingTiers = useMemo(() => {
    if (!stripePricesData?.products) {
      return [];
    }

    return stripePricesData.products
      .filter((product) => product.prices && product.prices.length > 0)
      .filter(
        (product) => SHOW_VIP_PROMOS || !VIP_TIER_NAME.test(product.name || ""),
      )
      .map((product) => {
        const price = product.prices[0];
        const unitAmount = price.unitAmount || 0;
        const interval = price.recurring?.interval || "month";
        const isRecurring = price.type === "recurring";
        const isSocial = product.name === PRODUCT_LEXI_SOCIAL;
        const isPromo = product.name === PRODUCT_MELBOURNE_CUP_PROMO;
        const isWeekPass =
          product.name === PRODUCT_WEEKPASS ||
          ((product.name || "").toLowerCase().includes("7") &&
            (product.name || "").toLowerCase().includes("day"));
        const isPro = product.name === PRODUCT_LEXI_PREMIUM;
        const copy = isSocial
          ? PLAN_COPY.social
          : isWeekPass
            ? PLAN_COPY.weekPass
            : isPro
              ? PLAN_COPY.pro
              : null;
        const priceLabel = isSocial ? "Free" : formatAmount(unitAmount);
        const termLabel = isSocial ? "" : isRecurring ? `/${interval}` : "once-off";
        const marketingFeatures = parseMarketingFeatures(product);

        return {
          name: product.name,
          price: priceLabel,
          term: termLabel,
          description: copy?.blurb || product.description || "",
          features:
            copy?.features ||
            (Array.isArray(marketingFeatures) ? marketingFeatures : []),
          buttonText: isPro
            ? `Start Lexi Pro ${priceLabel}${termLabel}`
            : copy?.button || "Get started",
          gtm: copy?.gtm || "pricing_plan_button-1",
          available: true,
          stripeKey: price.id,
          productId: product.id,
          isSocial,
          isPromo,
          isWeekPass,
          isPro,
          isRecurring,
          paymentLink: price.paymentLink || null,
        };
      });
  }, [stripePricesData]);

  const handlePricingButtonClick = (tier) => {
    logger.log("Pricing button clicked:", tier.name);

    // For Free (Lexi Social) plan, open auth modal with "free" preselected
    if (tier.isSocial) {
      setSelectedPlanForModal("free");
      setIsAuthModalOpen(true);
      return;
    }

    // For 7-Day Pass (Week Pass), open auth modal with "day-pass" preselected
    if (tier.isWeekPass) {
      setSelectedPlanForModal("day-pass");
      setIsAuthModalOpen(true);
      return;
    }

    // For Pro plan, open auth modal with "pro" preselected
    if (tier.isPro) {
      setSelectedPlanForModal("pro");
      setIsAuthModalOpen(true);
      return;
    }

    // If payment link exists, redirect to Stripe payment link
    if (tier.paymentLink) {
      window.location.href = tier.paymentLink;
      return;
    }

    // No payment link available - redirect to signup
    window.location.href = "/signup";
  };

  const buttonClass = (tier) =>
    tier.isPro
      ? "rounded-full bg-brand-pink text-white shadow-[0_0_0_3px_#fff,0_0_0_4px_#F0A7D5] hover:bg-[#E391C4]"
      : tier.isWeekPass
        ? "rounded-lg border border-brand-pink bg-white text-brand-pink hover:bg-[#FDF2F8]"
        : "rounded-lg border border-gray-200 bg-white text-[#0A0A0A] hover:border-[#F0A7D5]";

  return (
    <>
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        initialPlan={selectedPlanForModal}
      />

      <section
        id="pricing"
        className="relative overflow-hidden bg-[#FDF2F8] py-16 md:py-24"
      >
        <div className="container relative mx-auto max-w-6xl px-4">
          <p className="text-sm font-black uppercase tracking-[0.03em] text-brand-pink">
            Pricing
          </p>
          <h2 className="mt-2 text-3xl font-extrabold leading-[1.12] tracking-[-0.02em] text-[#0A0A0A] md:text-[2.5rem]">
            Start free. Go Pro for all 12 tips.
          </h2>
          <p className="mt-3 max-w-[58ch] text-base text-gray-600">
            Every plan covers every Australian thoroughbred race.
          </p>

          {isLoading && (
            <div className="mt-10 text-center text-gray-500">
              <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-brand-pink"></div>
              Loading pricing...
            </div>
          )}

          {error && (
            <div className="mt-10 text-center text-red-600">
              Failed to load pricing. Please try again later.
            </div>
          )}

          {!isLoading && !error && pricingTiers.length === 0 && (
            <div className="mt-10 text-center text-gray-500">
              No pricing plans available at the moment.
            </div>
          )}

          {!isLoading && !error && pricingTiers.length > 0 && (
            <div
              className="mt-8 grid items-stretch gap-6 md:mt-10 md:grid-cols-3 md:gap-5"
              data-testid="pricing-plans"
            >
              {pricingTiers.map((tier) => (
                <article
                  key={tier.productId || tier.name}
                  data-plan={tier.isPro ? "pro" : tier.isWeekPass ? "week-pass" : tier.isSocial ? "social" : "other"}
                  className={`relative flex min-w-0 flex-col gap-5 rounded-[14px] bg-white p-6 ${
                    tier.isPro
                      ? "border-2 border-brand-pink shadow-[0_24px_48px_-24px_rgba(243,0,153,0.35),0_10px_15px_-3px_rgba(0,0,0,0.08)]"
                      : "border border-gray-200"
                  }`}
                >
                  {tier.isPro && (
                    <span className="absolute -top-3 left-6 inline-flex items-center rounded-md bg-brand-pink px-2 py-0.5 text-xs font-bold text-white">
                      Best value
                    </span>
                  )}
                  <div>
                    <h3 className="text-lg font-extrabold tracking-[-0.01em] text-[#0A0A0A]">
                      {tier.name}
                    </h3>
                    <p className="mt-1.5 flex items-baseline gap-1.5">
                      <span className="text-4xl font-extrabold tracking-[-0.03em] text-[#0A0A0A] tabular-nums">
                        {tier.price}
                      </span>
                      {tier.term && (
                        <span className="text-sm font-semibold text-gray-500">
                          {tier.term}
                        </span>
                      )}
                    </p>
                    {tier.description && (
                      <p className="mt-1 text-[15px] text-gray-600">
                        {tier.description}
                      </p>
                    )}
                  </div>

                  {tier.features.length > 0 && (
                    <ul className="grid gap-2.5">
                      {tier.features.map((feature) => (
                        <li
                          key={feature}
                          className="flex items-start gap-2.5 text-[15px] leading-snug text-gray-800"
                        >
                          <Check
                            className="mt-px h-[18px] w-[18px] shrink-0 text-brand-pink"
                            strokeWidth={2}
                            aria-hidden="true"
                          />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="mt-auto">
                    <button
                      type="button"
                      onClick={() => handlePricingButtonClick(tier)}
                      data-gtm-element-id={tier.gtm}
                      className={`inline-flex min-h-[42px] w-full items-center justify-center px-5 text-[15px] font-bold transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-pink focus-visible:ring-offset-2 ${buttonClass(tier)}`}
                    >
                      {tier.buttonText}
                    </button>
                    {tier.isPro && (
                      <p className="mt-3 flex items-center justify-center gap-1.5 text-[13px] text-gray-600">
                        <Check
                          className="h-4 w-4 text-brand-pink"
                          strokeWidth={2}
                          aria-hidden="true"
                        />
                        Cancel anytime
                      </p>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}

          <p className="mt-6 text-center text-[13px] text-gray-500">
            18+ · Gamble responsibly · Gambling Help 1800 858 858
          </p>
        </div>
      </section>

      <Toaster position="bottom-center" />
    </>
  );
}

export default PricingSection;
